/**
 * Track Entity
 *
 * A pure domain entity representing a music track.
 * Contains business rules and invariants - no framework or persistence dependencies.
 */

export interface TrackMetadata {
  album?: string;
  genre?: string;
  year?: number;
  streamingLinks?: StreamingLinks;
}

export interface StreamingLinks {
  spotify?: string;
  appleMusic?: string;
  youtube?: string;
  soundcloud?: string;
}

export interface Track {
  readonly id: string;
  readonly title: string;
  readonly artist: string;
  readonly audioUrl: string;
  readonly coverUrl: string;
  readonly duration?: number; // in seconds
  readonly metadata?: TrackMetadata;
}

/**
 * Check if a track has all required fields to be valid.
 */
export function isValidTrack(track: Partial<Track>): track is Track {
  return Boolean(
    track.id &&
      track.title &&
      track.artist &&
      track.audioUrl &&
      track.coverUrl
  );
}

/**
 * Check if a track has any streaming links in its metadata.
 */
export function hasStreamingLinks(track: Track): boolean {
  const links = track.metadata?.streamingLinks;
  if (!links) return false;

  return Boolean(
    links.spotify || links.appleMusic || links.youtube || links.soundcloud
  );
}

/**
 * Get the display duration of a track in mm:ss format.
 */
export function getFormattedDuration(track: Track): string | null {
  if (track.duration === undefined || track.duration < 0) {
    return null;
  }

  const minutes = Math.floor(track.duration / 60);
  const seconds = Math.floor(track.duration % 60);
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

/**
 * Create a new Track entity.
 * Throws if required fields are missing.
 */
export function createTrack(input: {
  id: string;
  title: string;
  artist: string;
  audioUrl: string;
  coverUrl: string;
  duration?: number;
  metadata?: TrackMetadata;
}): Track {
  if (!isValidTrack(input)) {
    throw new Error('Track is missing required fields');
  }

  return {
    id: input.id,
    title: input.title,
    artist: input.artist,
    audioUrl: input.audioUrl,
    coverUrl: input.coverUrl,
    duration: input.duration,
    metadata: input.metadata,
  };
}
